import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AppService } from 'src/@core/services/app.service';
import { LoopService } from './loop.service';
import {
  actionLabel, anyActive, changeActions, ChangeAction, confirmText, fieldLabel, formatEpoch, formatTokens,
  isRunActive, LoopChange, LoopRun, readLoopConfig, replayText, riskLabel, RunSummary, scheduleText, sortRuns, statusLabel,
  summariseRun, testResultLabel, tokenShare, tokensText, triggerLabel
} from './loop.logic';

/** Poll every 5 seconds while a run is queued or running; give up after this many failures in a row. */
const POLL_MS = 5000;
const MAX_POLL_FAILURES = 10;

interface PendingDecision {
  changeId: string;
  action: ChangeAction;
}

/**
 * Loop tab: start a run, watch it, and review what it found and what it wants to
 * change. Changes are approved, rejected or rolled back here after a confirm step.
 */
@Component({
  selector: 'hour4u-admin-project-loop',
  templateUrl: './project-loop.component.html',
  styleUrls: ['./project-loop.component.scss']
})
export class ProjectLoopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectDetails: any;
  /** Asks the project view to open the Settings tab. */
  @Output() openSettings = new EventEmitter<void>();
  /** A change was applied or rolled back, so the project (prompts) should be reloaded. */
  @Output() changed = new EventEmitter<void>();

  profile = this.appService.getCurrentAccount();

  runs: RunSummary[] = [];
  selectedRunId = '';
  detail: LoopRun | null = null;

  isLoading = false;
  loadError = '';
  isDetailLoading = false;
  detailError = '';
  isStarting = false;
  startError = '';

  pending: PendingDecision | null = null;
  isDeciding = false;
  decideError = '';
  /** Change ids whose before/after block is open. */
  openDiffs: { [changeId: string]: boolean } = {};

  private requestId = 0;
  private detailRequestId = 0;
  private pollTimer: any = null;
  private pollFailures = 0;
  pollStopped = false;

  readonly statusLabel = statusLabel;
  readonly riskLabel = riskLabel;
  readonly fieldLabel = fieldLabel;
  readonly triggerLabel = triggerLabel;
  readonly formatEpoch = formatEpoch;
  readonly formatTokens = formatTokens;
  readonly tokensText = tokensText;
  readonly tokenShare = tokenShare;
  readonly replayText = replayText;
  readonly summariseRun = summariseRun;
  readonly testResultLabel = testResultLabel;
  readonly changeActions = changeActions;
  readonly actionLabel = actionLabel;
  readonly confirmText = confirmText;
  readonly isRunActive = isRunActive;

  constructor(private loopService: LoopService, private appService: AppService) {}

  ngOnInit() {
    this.load(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['projectDetails'];
    if (change && !change.firstChange) {
      const before = change.previousValue;
      const after = change.currentValue;
      if (!before || !after || before.project_id !== after.project_id) {
        this.stopPolling();
        this.runs = [];
        this.detail = null;
        this.selectedRunId = '';
        this.pending = null;
        this.pollStopped = false;
        this.load(true);
      }
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    // Late responses must not touch a destroyed view.
    this.requestId++;
    this.detailRequestId++;
  }

  get config() {
    return readLoopConfig(this.projectDetails);
  }

  /** Whether the "when does it run" panel is open. */
  showSchedule = false;

  /** When the loop runs by itself, in a sentence. */
  get scheduleSentence(): string {
    const ist = new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
    return scheduleText(this.config, this.projectDetails, ist);
  }

  /** Totals across every run in the list. */
  get totals() {
    let findings = 0;
    let proposed = 0;
    let applied = 0;
    let scheduled = 0;
    this.runs.forEach(r => {
      findings += r.findings_count || 0;
      proposed += r.changes_count || 0;
      applied += r.changes_applied || 0;
      if (r.trigger === 'scheduled') { scheduled++; }
    });
    return { runs: this.runs.length, findings, proposed, applied, scheduled };
  }

  /** "Looked at calls from the last 365 days" for a run's detail. */
  windowText(run: RunSummary): string {
    const days = run && run.stats ? run.stats.window_days : 0;
    return days ? 'Looked at calls from the last ' + days + ' days' + (days > 30 ? ' because the last 30 days had none.' : '.') : '';
  }

  get enabled(): boolean {
    return !!(this.projectDetails && this.projectDetails.loop_config && this.projectDetails.loop_config.enabled);
  }

  get activeRun(): RunSummary | null {
    const found = this.runs.filter(r => isRunActive(r.status))[0];
    return found || null;
  }

  get canRun(): boolean {
    return this.enabled && !this.activeRun && !this.isStarting;
  }

  /** The run on screen: what the person picked, otherwise the newest. */
  get shownRunId(): string {
    return this.selectedRunId || (this.runs.length ? this.runs[0].run_id : '');
  }

  private ready(): boolean {
    return !!(this.projectDetails && this.projectDetails.project_id && this.profile && this.profile.entity);
  }

  load(withDetail: boolean) {
    if (!this.ready()) { return; }
    const id = ++this.requestId;
    this.isLoading = true;
    this.loadError = '';
    this.loopService.getRuns(this.profile.entity.id, this.projectDetails.project_id).subscribe(
      (res) => {
        if (id !== this.requestId) { return; }
        this.isLoading = false;
        this.runs = sortRuns(res && res.runs ? res.runs : []);
        if (withDetail && this.shownRunId) {
          this.loadDetail(this.shownRunId);
        }
        this.schedulePollIfNeeded();
      },
      () => {
        if (id !== this.requestId) { return; }
        this.isLoading = false;
        this.loadError = 'Could not load the runs. Try again.';
      }
    );
  }

  selectRun(runId: string) {
    if (this.selectedRunId === runId && this.detail) { return; }
    this.selectedRunId = runId;
    this.detail = null;
    this.pending = null;
    this.decideError = '';
    this.openDiffs = {};
    this.loadDetail(runId);
    this.schedulePollIfNeeded();
  }

  closeDetail() {
    this.selectedRunId = '';
    this.detail = null;
    this.pending = null;
    this.detailRequestId++;
  }

  loadDetail(runId: string) {
    if (!this.ready() || !runId) { return; }
    const id = ++this.detailRequestId;
    this.isDetailLoading = !this.detail;
    this.detailError = '';
    this.loopService.getRun(this.profile.entity.id, this.projectDetails.project_id, runId).subscribe(
      (res) => {
        if (id !== this.detailRequestId) { return; }
        this.isDetailLoading = false;
        this.detail = res && res.run ? res.run : null;
        this.mergeSummary();
      },
      () => {
        if (id !== this.detailRequestId) { return; }
        this.isDetailLoading = false;
        this.detailError = 'Could not load this run. Try again.';
      }
    );
  }

  /** Keep the list row in step with the detail that was just loaded. */
  private mergeSummary() {
    const d = this.detail;
    if (!d) { return; }
    this.runs = this.runs.map(r => (r.run_id === d.run_id ? this.toSummary(d) : r));
  }

  private toSummary(d: LoopRun): RunSummary {
    return {
      run_id: d.run_id, status: d.status, trigger: d.trigger, created_at: d.created_at, started_at: d.started_at,
      finished_at: d.finished_at, summary: d.summary, error: d.error, stats: d.stats,
      findings_count: d.findings_count, changes_count: d.changes_count, changes_applied: d.changes_applied
    };
  }

  runNow() {
    if (!this.canRun || !this.ready()) { return; }
    this.isStarting = true;
    this.startError = '';
    this.loopService.startRun(this.profile, this.projectDetails.project_id).subscribe(
      (res) => {
        this.isStarting = false;
        this.selectedRunId = res && res.run_id ? res.run_id : '';
        this.detail = null;
        this.pollStopped = false;
        this.pollFailures = 0;
        this.load(true);
      },
      (err) => {
        this.isStarting = false;
        const message = err && err.error && err.error.message;
        this.startError = message || 'Could not start a run. Try again.';
        if (err && err.status === 409) {
          // A run is already going; show it.
          this.pollStopped = false;
          this.load(false);
        }
      }
    );
  }

  // Polling
  private schedulePollIfNeeded() {
    this.stopPolling();
    if (this.pollStopped) { return; }
    const shownActive = this.detail ? isRunActive(this.detail.status) : false;
    if (!anyActive(this.runs) && !shownActive) { return; }
    this.pollTimer = setTimeout(() => this.poll(), POLL_MS);
  }

  private stopPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private poll() {
    this.pollTimer = null;
    if (!this.ready()) { return; }
    const id = ++this.requestId;
    const runId = this.shownRunId;
    this.loopService.getRuns(this.profile.entity.id, this.projectDetails.project_id).subscribe(
      (res) => {
        if (id !== this.requestId) { return; }
        this.pollFailures = 0;
        this.runs = sortRuns(res && res.runs ? res.runs : []);
        const shown = this.detail;
        if (runId && (!shown || isRunActive(shown.status))) {
          this.loadDetail(runId);
        }
        this.schedulePollIfNeeded();
      },
      () => {
        if (id !== this.requestId) { return; }
        this.pollFailures++;
        if (this.pollFailures >= MAX_POLL_FAILURES) {
          this.pollStopped = true;
          this.loadError = 'Lost contact with the server, so live updates stopped.';
          return;
        }
        this.schedulePollIfNeeded();
      }
    );
  }

  retryPolling() {
    this.pollStopped = false;
    this.pollFailures = 0;
    this.load(true);
  }

  // Decisions
  toggleDiff(changeId: string) {
    this.openDiffs[changeId] = !this.openDiffs[changeId];
  }

  ask(change: LoopChange, action: ChangeAction) {
    this.pending = { changeId: change.change_id, action };
    this.decideError = '';
  }

  cancelAsk() {
    if (!this.isDeciding) { this.pending = null; }
  }

  isAsking(change: LoopChange, action?: ChangeAction): boolean {
    return !!this.pending && this.pending.changeId === change.change_id && (!action || this.pending.action === action);
  }

  confirm(change: LoopChange) {
    if (!this.pending || this.pending.changeId !== change.change_id || this.isDeciding || !this.detail || !this.ready()) {
      return;
    }
    const action = this.pending.action;
    const runId = this.detail.run_id;
    this.isDeciding = true;
    this.decideError = '';
    this.loopService.decideChange(this.profile, this.projectDetails.project_id, runId, change.change_id, action).subscribe(
      (res) => {
        this.isDeciding = false;
        this.pending = null;
        if (res && res.run && this.selectedRunIsOrWillBe(runId)) {
          this.detail = res.run;
          this.mergeSummary();
        }
        if (action !== 'reject') { this.changed.emit(); }
      },
      (err) => {
        this.isDeciding = false;
        const message = err && err.error && err.error.message;
        this.decideError = message || 'Could not save that decision. Try again.';
      }
    );
  }

  private selectedRunIsOrWillBe(runId: string): boolean {
    return !!this.detail && this.detail.run_id === runId;
  }

  trackByRun(_: number, r: RunSummary) { return r.run_id; }
  trackByChange(_: number, c: LoopChange) { return c.change_id; }
  trackByIndex(i: number) { return i; }
}
